import type { SimpleTableConfig } from "@/components/shared/UniversalCRMTable/types";
import type { Email_Campaigns } from "@/src/gql/graphql";
import type { EntityFormSectionConfig } from "@/lib/types/forms/entity-form-sections";
import { emailCampaignDetailActions } from "./EmailCampaignDetailConfig";
import { emailCampaignSavedViews } from "@/features/email-campaigns/config/saved-views";
import {
  EMAIL_CAMPAIGN_STATUS_OPTIONS,
  EMAIL_CAMPAIGN_BRAND_OPTIONS,
} from "@/features/email-campaigns/config/options";
import {
  textField,
  selectField,
  numberField,
  datetimeField,
  STANDARD_TABLE_FEATURES,
  standardCrmBulkActions,
} from "@/features/crm-table-config/table-config-builders";

/**
 * Table config for the `email_campaigns` entity — modeled on
 * `EnhancedSocialMediaPostsConfig.ts`. This `fields` map is the single source
 * of truth for both list columns AND the detail modal's "fields" tab.
 *
 * Campaigns are authored + delivered by the marketing send pipeline
 * (/api/cron/email-campaign-send). The CRM surface is read-mostly (view +
 * status correction + delete), so `actions.create` is false.
 *
 * The `recipients_count` / `sent_count` / `opened_count` / `clicked_count`
 * fields are SYNTHETIC read-only columns projected from the `stats` jsonb by
 * the entity definition's `synthesizeField` (see entities/email-campaigns.ts).
 */

/** Progressive-disclosure create-form layout (create is not the primary path). */
const emailCampaignCreateFormSections = (): readonly EntityFormSectionConfig[] => [
  {
    id: "core",
    kind: "always",
    title: "Campaign",
    fieldNames: ["name", "subject", "status", "brand"],
  },
  {
    id: "sender",
    kind: "collapsible",
    title: "Sender",
    fieldNames: ["from_name", "from_email", "reply_to"],
  },
  {
    id: "scheduling",
    kind: "collapsible",
    title: "Scheduling",
    fieldNames: ["scheduled_at", "sent_at"],
  },
];

export const EMAIL_CAMPAIGNS_CONFIG: SimpleTableConfig<Email_Campaigns> = {
  entity: "email_campaigns",
  displayName: "Email Campaign",
  statusFieldId: "status",
  defaultPageSize: 25,
  defaultSort: { field: "scheduled_at", direction: "desc" },
  rowActionsPlacement: "end",
  rowActionsDisplayMode: "hover",

  fields: {
    name: textField({
      label: "Name",
      sortable: true,
      filterable: true,
      searchable: true,
      resizable: true,
      hideable: true,
      editable: false,
      primary: true,
      filterType: "contains",
      width: 240,
      minWidth: 160,
      maxWidth: 400,
      mobileDetailSectionId: "core",
      icon: "FileText",
    }),
    subject: textField({
      label: "Subject",
      sortable: true,
      filterable: true,
      searchable: true,
      resizable: true,
      hideable: true,
      editable: false,
      filterType: "contains",
      width: 280,
      minWidth: 180,
      maxWidth: 480,
      mobileDetailSectionId: "core",
      icon: "Mail",
    }),
    status: selectField({
      label: "Status",
      isStatusField: true,
      sortable: true,
      filterable: true,
      resizable: true,
      hideable: true,
      editable: false,
      badge: true,
      groupable: true,
      filterType: "exact",
      width: 150,
      minWidth: 120,
      maxWidth: 200,
      options: EMAIL_CAMPAIGN_STATUS_OPTIONS.map((o) => ({ label: o.label, value: o.value })),
      mobileDetailSectionId: "core",
      icon: "CircleDot",
    }),
    brand: selectField({
      label: "Brand",
      sortable: true,
      filterable: true,
      resizable: true,
      hideable: true,
      editable: false,
      badge: true,
      groupable: true,
      filterType: "exact",
      width: 200,
      minWidth: 140,
      maxWidth: 300,
      options: EMAIL_CAMPAIGN_BRAND_OPTIONS.map((o) => ({ label: o.label, value: o.value })),
      mobileDetailSectionId: "core",
      icon: "Building2",
    }),
    from_name: textField({
      label: "From Name",
      sortable: false,
      filterable: false,
      searchable: false,
      resizable: true,
      hideable: true,
      editable: false,
      width: 180,
      minWidth: 120,
      maxWidth: 280,
      mobileDetailSectionId: "sender",
      icon: "User",
    }),
    from_email: textField({
      label: "From Email",
      sortable: true,
      filterable: true,
      searchable: true,
      resizable: true,
      hideable: true,
      editable: false,
      filterType: "contains",
      width: 220,
      minWidth: 160,
      maxWidth: 360,
      mobileDetailSectionId: "sender",
      icon: "AtSign",
    }),
    reply_to: textField({
      label: "Reply-To",
      sortable: false,
      filterable: false,
      searchable: false,
      resizable: true,
      hideable: true,
      editable: false,
      width: 220,
      minWidth: 160,
      maxWidth: 360,
      mobileDetailSectionId: "sender",
      icon: "Reply",
    }),
    scheduled_at: datetimeField({
      label: "Scheduled",
      sortable: true,
      filterable: true,
      resizable: true,
      hideable: true,
      editable: false,
      filterType: "dateRange",
      width: 170,
      minWidth: 130,
      maxWidth: 220,
      mobileDetailSectionId: "scheduling",
      icon: "Calendar",
    }),
    sent_at: datetimeField({
      label: "Sent",
      sortable: true,
      filterable: true,
      resizable: true,
      hideable: true,
      editable: false,
      filterType: "dateRange",
      width: 170,
      minWidth: 130,
      maxWidth: 220,
      mobileDetailSectionId: "scheduling",
      icon: "CalendarCheck",
    }),
    recipients_count: numberField({
      label: "Recipients",
      sortable: false,
      filterable: false,
      searchable: false,
      resizable: true,
      hideable: true,
      editable: false,
      width: 120,
      minWidth: 90,
      maxWidth: 160,
      mobileDetailSectionId: "engagement",
      icon: "Users",
    }),
    sent_count: numberField({
      label: "Sent",
      sortable: false,
      filterable: false,
      searchable: false,
      resizable: true,
      hideable: true,
      editable: false,
      width: 110,
      minWidth: 80,
      maxWidth: 150,
      mobileDetailSectionId: "engagement",
      icon: "Send",
    }),
    opened_count: numberField({
      label: "Opened",
      sortable: false,
      filterable: false,
      searchable: false,
      resizable: true,
      hideable: true,
      editable: false,
      width: 110,
      minWidth: 80,
      maxWidth: 150,
      mobileDetailSectionId: "engagement",
      icon: "Eye",
    }),
    clicked_count: numberField({
      label: "Clicked",
      sortable: false,
      filterable: false,
      searchable: false,
      resizable: true,
      hideable: true,
      editable: false,
      width: 110,
      minWidth: 80,
      maxWidth: 150,
      mobileDetailSectionId: "engagement",
      icon: "MousePointerClick",
    }),
  },

  features: { ...STANDARD_TABLE_FEATURES },

  actions: {
    create: false,
    edit: true,
    delete: true,
    view: true,
  },

  detailActions: emailCampaignDetailActions,
  bulkActions: standardCrmBulkActions<Email_Campaigns>(),
  savedViews: emailCampaignSavedViews,
  createFormSections: emailCampaignCreateFormSections,
  customFilterHooks: {},

  mobileDetailSections: [
    { id: "core", label: "Campaign" },
    { id: "sender", label: "Sender" },
    { id: "scheduling", label: "Scheduling" },
    { id: "engagement", label: "Engagement" },
  ],
};
